import { call, put, select, takeLatest } from 'redux-saga/effects';

import { hubspotUrl, monolithApiUrl, outlookUrl, sfUrl } from '../../config/app';
import { BLUEBIRD, GMAIL, HUBSPOT, NONE, OUTLOOK, SALESFORCE } from '../../constants/integrations';
import { crmEndpoints, endpoints, routes } from '../../constants/routing';
import { ISearchLeadsAndContactsPayload } from '../../types/integrations';
import { IPromiseCallbacks, IReduxAction } from '../../types/redux';
import { googleAuthorize } from '../../utils/google';
import { PopupProvider } from '../../utils/PopupProvider';
import { handleApiError } from '../../utils/ui';
import * as IntegrationsActions from '../actions/integrations';
import { selectCrmClientId } from '../selectors/integrations';
import { selectCurrentOrganizationCRMType, selectCurrentOrganizationEmailType } from '../selectors/organizations';
import { AuthRequest } from './helpers';

export function* crmStartOAuthFlowWorkerSaga(action?: IReduxAction<IPromiseCallbacks>): Generator<any, any, any> {
  const crmType = yield select(selectCurrentOrganizationCRMType);

  switch (crmType) {
    case SALESFORCE: {
      yield call(sfOAuthFlowWorkerSaga, action);
      break;
    }
    case HUBSPOT: {
      yield call(hubspotOAuthFlowWorkerSaga, action);
      break;
    }
    case NONE:
    default:
      yield put(IntegrationsActions.crmOAuthEnd());
  }
}

export function* sfOAuthFlowWorkerSaga(action?: IReduxAction<IPromiseCallbacks>): Generator<any, any, any> {
  const { resolve, reject } = action?.payload || {};

  try {
    const clientId = yield select(selectCrmClientId);

    if (!clientId) {
      const error = { message: 'Salesforce client ID is not available. Please contact support.' };
      yield put(IntegrationsActions.crmOAuthFailure(error));
      if (typeof reject === 'function') {
        reject(error);
      }
      return;
    }

    const redirectUrl = `${window.location.origin}${routes.sfAuthorizeCallback}`;
    const url = `${sfUrl}${crmEndpoints.authorizeSF}?client_id=${clientId}&redirect_uri=${redirectUrl}&response_type=code`;

    const popupResponse = yield call<any>(new PopupProvider().openPopUpInWindow, { url });

    if (!popupResponse.ok) {
      yield put(IntegrationsActions.crmOAuthFailure(popupResponse.error));
      if (typeof reject === 'function') {
        reject(popupResponse.error);
      }
    } else {
      const body = { redirect_url: redirectUrl, code: popupResponse.data, provider: SALESFORCE };
      const authResponse = yield call<any>(AuthRequest, {
        endpoint: `${monolithApiUrl}${endpoints.exchangeAuth}`,
        body: JSON.stringify(body),
        method: 'POST',
      });
      if (!authResponse.ok) {
        yield put(IntegrationsActions.crmOAuthFailure(authResponse.error));
        if (typeof reject === 'function') {
          reject(authResponse.error);
        }
      } else {
        if (typeof resolve === 'function') {
          resolve();
        }

        yield put(IntegrationsActions.crmOAuthSuccess(authResponse.body));
        yield put(IntegrationsActions.checkIntegrationsRequest());
      }
    }
  } catch (e) {
    // yield put(trackException(e));
    yield put(IntegrationsActions.crmOAuthFailure(e));
    if (typeof reject === 'function') {
      reject(e);
    }
  } finally {
    yield put(IntegrationsActions.crmOAuthEnd());
  }
}

export function* searchSaga(action: IReduxAction<ISearchLeadsAndContactsPayload>): Generator<any, any, any> {
  const { query: q, limit = 10, crm_type, signal, resolve, reject } = action.payload || {};

  try {
    const queryParams = { q, limit };
    const endpoint = `${monolithApiUrl}${endpoints.getLeadsAndContacts}`;

    const response = yield call<any>(AuthRequest, { endpoint, headers: { crm_type }, queryParams, signal });
    if (!response.ok) {
      if (reject) {
        reject(response.body);
      }
      yield put(IntegrationsActions.searchLeadsAndContactsFailure(response.body));
    } else {
      if (!Array.isArray(response.body)) {
        const message = 'User does not exist in the CRM system';
        yield call(handleApiError(message));
        yield put(IntegrationsActions.searchLeadsAndContactsFailure(message));
        return;
      }

      if (resolve) {
        resolve(response.body);
      }
      yield put(IntegrationsActions.searchLeadsAndContactsSuccess(response.body));
    }
  } catch (error) {
    yield call(handleApiError('Failed to fetch leads and contacts'));
    yield put(IntegrationsActions.searchLeadsAndContactsFailure(error));
  }
}

export function* opportunitiesSaga(action: any): Generator<any, any, any> {
  const { cid, crm_type } = action.payload || {};
  const { resolve, reject } = action.options || {};

  try {
    const endpoint = `${monolithApiUrl}${endpoints.getOpportunities}`;
    const queryParams = { cid };

    const response = yield call<any>(AuthRequest, { endpoint, queryParams, headers: { crm_type } });
    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(IntegrationsActions.getOpportunitiesFailure(response.body));
    } else {
      if (typeof resolve === 'function') {
        resolve(response.body);
      }
      yield put(IntegrationsActions.getOpportunitiesSuccess(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(IntegrationsActions.getOpportunitiesFailure(error));
  }
}

export function* hubspotOAuthFlowWorkerSaga(action?: IReduxAction<IPromiseCallbacks>): Generator<any, any, any> {
  const { resolve, reject } = action?.payload || {};

  try {
    const clientId = process.env.REACT_APP_HUBSPOT_CLIENT_ID;
    const scopes = process.env.REACT_APP_HUBSPOT_AUTH_SCOPES;

    if (clientId) {
      const redirectUrl = `${window.location.origin}${routes.hubspotAuthorizeCallback}`;
      const url = `${hubspotUrl}${crmEndpoints.authorizeHubspot}?client_id=${clientId}&redirect_uri=${redirectUrl}&scope=${scopes}`;

      const popupResponse = yield call<any>(new PopupProvider().openPopUpInWindow, { url });

      if (!popupResponse.ok) {
        yield put(IntegrationsActions.crmOAuthFailure(popupResponse.error));
        if (typeof reject === 'function') {
          reject(popupResponse.error);
        }
      } else {
        const body = { redirect_url: redirectUrl, code: popupResponse.data, provider: HUBSPOT };
        const authResponse = yield call<any>(AuthRequest, {
          endpoint: `${monolithApiUrl}${endpoints.exchangeAuth}`,
          body: JSON.stringify(body),
          method: 'POST',
        });

        if (!authResponse.ok) {
          yield put(IntegrationsActions.crmOAuthFailure(authResponse.error));
          if (typeof reject === 'function') {
            reject(authResponse.error);
          }
        } else {
          if (typeof resolve === 'function') {
            resolve();
          }
          yield put(IntegrationsActions.crmOAuthSuccess(authResponse.body));
          yield put(IntegrationsActions.checkIntegrationsRequest());
        }
      }
    } else {
      yield put(IntegrationsActions.crmOAuthFailure({ message: 'No hubspot client_ID found' }));
    }
  } catch (e) {
    // yield put(trackException(e));
    if (typeof reject === 'function') {
      reject(e);
    }
    yield put(IntegrationsActions.crmOAuthFailure(e));
  } finally {
    yield put(IntegrationsActions.crmOAuthEnd());
  }
}

export function* emailStartOAuthFlowWorkerSaga(): Generator<any, any, any> {
  const emailType = yield select(selectCurrentOrganizationEmailType);

  switch (emailType) {
    case GMAIL: {
      yield call(gmailStartOAuthWorkerSaga);
      break;
    }
    case OUTLOOK: {
      yield call(outlookStartOAuthWorkerSaga);
      break;
    }
    case BLUEBIRD:
    default:
      yield put(IntegrationsActions.emailOAuthEnd());
  }
}

export function* gmailStartOAuthWorkerSaga(): Generator<any, any, any> {
  try {
    const { code, error }: any = yield call(googleAuthorize);

    if (error) {
      yield put(IntegrationsActions.emailOAuthFailure(error));
    } else if (code) {
      const endpoint = `${monolithApiUrl}${endpoints.exchangeAuth}`;
      // Postmessage redirect_url should be used for the gapi grantOfflineAccess() method
      const body = { code, redirect_url: 'postmessage', provider: GMAIL };

      const authResponse = yield call<any>(AuthRequest, {
        endpoint,
        body: JSON.stringify(body),
        method: 'POST',
      });

      if (authResponse.ok) {
        yield put(IntegrationsActions.emailOAuthSuccess(authResponse.body));
        yield put(IntegrationsActions.checkIntegrationsRequest());
      } else {
        yield put(IntegrationsActions.emailOAuthFailure(authResponse.body));
      }
    }
  } catch (error) {
    yield put(IntegrationsActions.emailOAuthFailure(error));
  } finally {
    yield put(IntegrationsActions.emailOAuthEnd());
  }
}

function* outlookStartOAuthWorkerSaga(): Generator<any, any, any> {
  try {
    const queryParams = new URLSearchParams({
      client_id: process.env.REACT_APP_OUTLOOK_CLIENT_ID || '',
      scope: 'offline_access mail.send',
      response_type: 'code',
      redirect_uri: `${window.location.origin}${routes.outlookAuthorizeCallback}`,
      response_code: 'query',
      state: Math.round(Math.random() * 100000).toString(),
    });

    const url = `${outlookUrl}${crmEndpoints.authorizeOutlook}?${queryParams.toString()}`;

    const popupResponse = yield call<any>(new PopupProvider().openPopUpInWindow, { url });

    if (!popupResponse.ok) {
      yield put(IntegrationsActions.emailOAuthFailure(popupResponse.error));
    } else {
      const endpoint = `${monolithApiUrl}${endpoints.exchangeAuth}`;
      const body = { redirect_url: queryParams.get('redirect_uri'), code: popupResponse.data, provider: OUTLOOK };
      const authResponse = yield call<any>(AuthRequest, { endpoint, body: JSON.stringify(body), method: 'POST' });
      if (!authResponse.ok) {
        yield put(IntegrationsActions.emailOAuthFailure(authResponse.body));
      } else {
        yield put(IntegrationsActions.emailOAuthSuccess(authResponse.body));
        yield put(IntegrationsActions.checkIntegrationsRequest());
      }
    }
  } catch (error) {
    yield put(IntegrationsActions.emailOAuthFailure(error));
  } finally {
    yield put(IntegrationsActions.emailOAuthEnd());
  }
}

export function* generateZapierApiKeyWorkerSaga(action: IReduxAction<IPromiseCallbacks>): Generator<any, any, any> {
  const { resolve, reject } = action.payload || {};

  try {
    const endpoint = `${monolithApiUrl}${endpoints.generateAPIKey}`;

    const response = yield call<any>(AuthRequest, { endpoint, method: 'POST' });

    if (response.ok) {
      if (typeof resolve === 'function') {
        resolve(response.body);
      }
      yield put(IntegrationsActions.generateZapierApiKeySuccess());
    } else {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(IntegrationsActions.generateZapierApiKeyFailure(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(IntegrationsActions.generateZapierApiKeyFailure(error));
  }
}

export function* getUserApiKeyWorkerSaga(action: IReduxAction<IPromiseCallbacks>): Generator<any, any, any> {
  const { resolve, reject } = action.payload || {};

  try {
    const endpoint = `${monolithApiUrl}${endpoints.getUserApiKey}`;
    const response = yield call(AuthRequest, {
      method: 'GET',
      endpoint,
    });

    if (response.ok) {
      const { api_key } = response.body || {};

      if (typeof resolve === 'function') {
        resolve(api_key);
      }
      yield put(IntegrationsActions.getUserApiKeySuccess(api_key));
    } else {
      if (typeof reject === 'function') {
        reject(response.body);
      }

      yield put(IntegrationsActions.getUserApiKeyFailure(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(IntegrationsActions.getUserApiKeyFailure(error));
  }
}

export function* installHubspotAppWorkerSaga(
  action: IReduxAction<{
    code: string;
    redirectUrl: string;
    resolve?: (data: any) => void;
    reject?: (error: any) => void;
  }>,
): Generator<any, any, any> {
  const { code, redirectUrl, resolve, reject } = action.payload || {};

  try {
    const endpoint = `${monolithApiUrl}${endpoints.installHubspotApp}`;
    const queryParams = { code, redirect_uri: redirectUrl };

    const response = yield call<any>(AuthRequest, { endpoint, method: 'GET', queryParams });

    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(IntegrationsActions.installHubspotAppFailure(response.body));
    } else {
      if (typeof resolve === 'function') {
        resolve(response.body);
      }
      yield put(IntegrationsActions.installHubspotAppSuccess(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(IntegrationsActions.installHubspotAppFailure(error));
  }
}

function* checkIntegrationsWorkerSaga(action?: IReduxAction<IPromiseCallbacks>): Generator<any, any, any> {
  const { resolve, reject } = action?.payload || {};

  try {
    const endpoint = `${monolithApiUrl}${endpoints.checkIntegrations}`;
    const response = yield call<any>(AuthRequest, { endpoint });

    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(IntegrationsActions.checkIntegrationsFailure(response.body));
    } else {
      if (typeof resolve === 'function') {
        resolve(response.body);
      }
      yield put(IntegrationsActions.checkIntegrationsSuccess(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(IntegrationsActions.checkIntegrationsFailure(error));
  }
}

const sagas = {
  *watchSFOAuthStartWatcher() {
    yield takeLatest(IntegrationsActions.CRM_OAUTH_START, crmStartOAuthFlowWorkerSaga);
  },
  *searchLeadsAndContactsRequest() {
    yield takeLatest(IntegrationsActions.SEARCH_LEADS_AND_CONTACTS_REQUEST, searchSaga);
  },
  *getOpportunitiesRequest() {
    yield takeLatest(IntegrationsActions.GET_OPPORTUNITIES_REQUEST, opportunitiesSaga);
  },
  *watchEmailOAuthWatcher() {
    yield takeLatest(IntegrationsActions.EMAIL_OAUTH_START, emailStartOAuthFlowWorkerSaga);
  },
  *watchZapierApyKeyWatcher() {
    yield takeLatest(IntegrationsActions.GENERATE_ZAPIER_API_KEY_REQUEST, generateZapierApiKeyWorkerSaga);
  },
  *watchUserApiKeySagaRequest() {
    yield takeLatest(IntegrationsActions.GET_USER_API_KEY_REQUEST, getUserApiKeyWorkerSaga);
  },
  *watchInstallHubspotAppSaga() {
    yield takeLatest(IntegrationsActions.INSTALL_HUBSPOT_APP, installHubspotAppWorkerSaga);
  },
  *watchCheckIntegrationsRequest() {
    yield takeLatest(IntegrationsActions.CHECK_INTEGRATIONS_REQUEST, checkIntegrationsWorkerSaga);
  },
};

export default sagas;
